import { isMobile } from '@databases/modes';
import type React from 'react';
import { useEffect } from 'react';
import { useIntl } from 'react-intl-next';
import { useSnapshot } from 'valtio';

import { currentConnectionStatus } from '@canvas/canvas-state/connection-status';

import { messages } from '../src/i18n.ts';

import { useContentStatusHeader } from './header/useContentStatusHeader.ts';
import { useContentVersionHeader } from './header/useContentVersionHeader.ts';

export const ContentStatusObserver: React.FC<{
  updateContentStatus?: ({
    status,
    tooltipText
  }: {
    status: string;
    tooltipText: string | undefined;
  }) => void;
  contentId: string;
  versionName: string | undefined;
  versionId: string | undefined;
}> = ({ updateContentStatus, contentId, versionName, versionId }) => {
  const { formatMessage } = useIntl();
  const isMobileMode = isMobile();

  const snackbarMessages = {
    online: formatMessage(
      isMobileMode
        ? messages['snackbar-message.online-mobile']
        : messages['snackbar-message.online']
    ),
    offline: formatMessage(
      isMobileMode
        ? messages['snackbar-message.offline-mobile']
        : messages['snackbar-message.offline']
    ),
    sessionLocked: formatMessage(messages['snackbar-message.sessionLocked.non-final'], {
      contentType: 'database'
    }),
    readOnlyModeActive: formatMessage(messages['snackbar-message.readOnlyModeActive']),
    viewToEditAccess: formatMessage(messages['snackbar-message.viewToEditAccess']),
    editToViewAccess: formatMessage(messages['snackbar-message.editToViewAccess']),
    editToViewOfflineChangesDiscarded: formatMessage(
      messages['snackbar-message.editToViewOfflineChangesDiscarded']
    )
  };

  const contentStatus = useContentStatusHeader(contentId, snackbarMessages);
  const { boardSyncStatus: contentSyncStatus } = useSnapshot(currentConnectionStatus);

  useContentVersionHeader(versionName, versionId);

  useEffect(() => {
    if (contentStatus && updateContentStatus) {
      updateContentStatus({
        status: contentStatus.status,
        tooltipText: contentStatus.tooltip
      });
    }
  }, [contentStatus, updateContentStatus, contentSyncStatus]);

  return null;
};
